import React, { useEffect, useState } from 'react';
import { Grid, Box, Button, Card, CardContent, Divider, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { setNestedObjectValues } from 'formik';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { TabContext, TabPanel } from '@mui/lab';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styles } from './styles';
import PositionDetailsTab from './PositionDetailsTab';
import DemandAttributes from '../../demandAttributes';
import {
    usePreCreatePositionMutation,
    useCreatePositionMutation,
    useCreatePositionWithCompaFieldsMutation,
} from '../../../api/positions';
import { useCustomAttributesFormik, usePersonalDetailsFormik } from './formik';
import { hasPermission } from '../../../utils/permission';

const views = [
    {
        label: 'Position Details',
        value: 'position-details',
    },
    {
        label: 'Custom Attributes',
        value: 'custom-attributes',
    },
];

const AddPosition = (props) => {
    const [view, setView] = useState('position-details');
    const [positionDetails, setPositionDetails] = useState({});
    const [preCreationDetails, setPreCreationDetails] = useState();
    const currentUser = useSelector((state) => state.session.user);
    const navigate = useNavigate();

    if (currentUser?.isClientHqEnabled) {
        return <Navigate to="/access-denied" />;
    }

    const authorities = useSelector((state) => state.session.user.authorities);
    const userHasCompaCreatePermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_CREATE_POSITION',
    ]);

    const personalDetailsFormik = usePersonalDetailsFormik(null, (value) => handleBasicDetails(value));
    const { customAttributesFormik, metaCustomFieldDefinitions: metaCustomFields } = useCustomAttributesFormik(
        preCreationDetails?.extendedDef,
        null,
        (value) => handleCustomDetails(value),
    );

    const { enqueueSnackbar } = useSnackbar();

    const [preCreatePosition, { isLoading: isPreCreateLoading }] = usePreCreatePositionMutation();
    const [createPosition, { isLoading: isCreateLoading }] = useCreatePositionMutation();
    const [createPositionWithCompaFields, { isLoading: isCreateWithCompaLoading }] =
        useCreatePositionWithCompaFieldsMutation();

    useEffect(() => {
        async function fetchPreCreationData() {
            try {
                const response = await preCreatePosition().unwrap();
                if (!response) return;
                if (response.status === 'ERROR') throw new Error(response?.message);

                setPreCreationDetails(response);
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Failed to fetch pre-creation data', { variant: 'error' });
            }
        }
        fetchPreCreationData();
    }, [preCreatePosition, enqueueSnackbar]);

    const handleBasicDetails = (details) => {
        setPositionDetails(details);
        setView('custom-attributes');
    };

    const handleCustomDetails = async (details) => {
        if (isCreateLoading || isCreateWithCompaLoading) return;
        const { submit, ...filteredData } = {
            ...positionDetails,
        };
        const customAttributesMetaData = isEmpty(metaCustomFields)
            ? []
            : [
                  {
                      attribute: 'customObjectInstance.customerID',
                      value: metaCustomFields.customerID,
                  },
                  {
                      attribute: 'customObjectInstance.baseObjectMetaClassName',
                      value: metaCustomFields.baseObjectMetaClassName,
                  },
                  {
                      attribute: 'customObjectInstance.metaCustomObjectDefinitionUID',
                      value: metaCustomFields.metaCustomObjectDefinitionID,
                  },
              ];

        let index = 0;
        (metaCustomFields?.metaCustomFieldDefinitions || []).forEach((customField) => {
            customAttributesMetaData.push(
                {
                    attribute: `customObjectInstance.customFieldValues[${index}].fieldValue`,
                    value: details[customField.fieldLabel] || '',
                },
                {
                    attribute: `customObjectInstance.customFieldValues[${index}].metaCustomFieldDefinitionID`,
                    value: customField.metaCustomFieldDefinitionID,
                },
            );
            index++;
        });

        const bodyFormData = new FormData();
        for (const key in filteredData) {
            bodyFormData.append(key, filteredData[key]);
        }

        customAttributesMetaData.forEach((customAttribute) => {
            bodyFormData.append(customAttribute.attribute, customAttribute.value);
        });

        try {
            const response = userHasCompaCreatePermisson
                ? await createPositionWithCompaFields(bodyFormData).unwrap()
                : await createPosition(bodyFormData).unwrap();

            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar('Position created successfully', { variant: 'success' });
            navigate('/positions');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Failed to create position', { variant: 'error' });
        }
    };

    const validateTabChange = async (value) => {
        if (value === 'custom-attributes') {
            setPositionDetails({
                ...positionDetails,
                ...personalDetailsFormik.values,
            });
        }
        const tabFormikMap = {
            'position-details': personalDetailsFormik,
            'custom-attributes': customAttributesFormik,
        };

        const errors = await tabFormikMap[view].validateForm();
        if (Object.keys(errors || {}).length === 0) {
            setView(value);
        } else {
            tabFormikMap[view].setTouched(setNestedObjectValues(errors, true));
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Positions</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card {...props}>
                    <CardContent>
                        {isPreCreateLoading ? (
                            <Box
                                sx={{
                                    py: 4,
                                }}
                            >
                                <Skeleton height={42} />
                                <Skeleton height={42} />
                                <Skeleton height={42} />
                            </Box>
                        ) : (
                            <>
                                <Grid container direction="column" spacing={2}>
                                    <Grid item>
                                        <Button
                                            startIcon={<ChevronLeftIcon />}
                                            variant="text"
                                            color="secondary"
                                            onClick={() => navigate('/positions')}
                                        >
                                            Back
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Typography color="textPrimary" variant="h4">
                                            Create Position
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <TabContext value={view}>
                                            <Box>
                                                <Tabs
                                                    value={view}
                                                    onChange={(event, value) => validateTabChange(value)}
                                                    aria-label="basic tabs example"
                                                >
                                                    {views.map((option) => (
                                                        <Tab
                                                            key={option.label}
                                                            disabled={false}
                                                            label={option.label}
                                                            value={option.value}
                                                        />
                                                    ))}
                                                </Tabs>
                                                <Divider sx={styles.divider} />
                                            </Box>
                                            <TabPanel sx={styles.tabpanel} value={'position-details'}>
                                                <PositionDetailsTab
                                                    isCreate
                                                    formik={personalDetailsFormik}
                                                    preCreationDetails={preCreationDetails}
                                                />
                                            </TabPanel>
                                            <TabPanel sx={styles.tabpanel} value={'custom-attributes'}>
                                                <DemandAttributes
                                                    formik={customAttributesFormik}
                                                    metaCustomFields={metaCustomFields}
                                                />
                                            </TabPanel>
                                        </TabContext>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default AddPosition;
